import { Location } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import * as moment from "moment";
import { NgxSpinnerService } from "ngx-spinner";
import { Simulator } from "src/app/models/simulator-model";
import { Studio } from "src/app/models/studio-model";
import { AuthService } from "src/app/services/auth/auth.service";
import { ReservationService } from "src/app/services/reservation/reservation.service";
import { StudioService } from "src/app/services/studio/studio.service";
import { GenerateErrors } from "src/app/utils/generate-errors";
import { Webflow } from "src/app/utils/webflow";
import { environment } from "src/environments/environment";
import Swal from "sweetalert2";

import { loadStripe } from "@stripe/stripe-js";

@Component({
  selector: "app-payment-form",
  templateUrl: "./payment-form.component.html",
  styleUrls: ["./payment-form.component.scss"],
})
export class PaymentFormComponent implements OnInit {
  loading: boolean = true;
  is_connected: boolean = true;
  currentStudio: Studio = null;
  params: any;
  nbr_hours = 4;
  errors = null;

  ph: number = 0;
  nh: number = 0;
  final_price: number = null;
  price_fees: any;

  stripe: any;

  final_price_after_apply_promo: number = 0;
  reduce_value: string = "0";
  promo_code_is_valid: boolean = false;

  discount_periods_found: boolean = false;

  promo_code_value: string = "";

  constructor(
    private router: Router,
    private studioService: StudioService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private authService: AuthService,
    private reservationService: ReservationService
  ) {}

  ngOnInit(): void {
    this.loadStripe();

    this.authService.currentUserSubject.subscribe((currentUserValue) => {
      this.is_connected = currentUserValue != null;
    });
    this.activatedRoute.queryParams.subscribe((params) => {
      this.params = { ...params }; // Create a shallow copy of params

      // prevent user to change data in the url
      let validStoredData =
        JSON.stringify(this.params) === localStorage.getItem("booking");

      this.promo_code_value =
        params.promo_code != undefined ? params.promo_code : "";
      if (
        params.start_date &&
        params.end_date &&
        params.studio &&
        validStoredData
      ) {
        // Parse strings into Moment objects
        const startMoment = moment(params.start_date, "YYYY-MM-DD HH:mm");
        const endMoment = moment(params.end_date, "YYYY-MM-DD HH:mm");

        // Calculate the difference in hours
        this.nbr_hours = endMoment.diff(startMoment, "hours", true);

        this.studioService
          .get_one(params.studio)
          .subscribe({
            next: (studio) => {
              this.currentStudio = Studio.attributeDataToJson(studio);
              this.calculate_price();
              Webflow.setIFrameHeight();
            },
            error: (error) => {
              if (GenerateErrors.check_if_is_custom_error(error)) {
                this.errors = GenerateErrors.get_message_error(error);
              } else {
                this.errors = "Le studio n'est pas disponible pour le moment";
              }
              this.currentStudio = null;
              Webflow.setIFrameHeight();
            },
          })
          .add(() => {
            this.loading = false;
          });
      } else {
        this.location.back();
      }
    });
  }

  async loadStripe() {
    this.stripe = await loadStripe(environment.stripe_pk);
  }

  calculate_price() {
    const startDateTime = moment(this.params?.start_date, "YYYY-MM-DD HH:mm");
    const endDateTime = moment(this.params?.end_date, "YYYY-MM-DD HH:mm");

    var obj = {
      ph: this.currentStudio.price,
      nh: Number(this.nbr_hours),
      studio: this.currentStudio?.id.toString(),
      start_date: startDateTime.format("YYYY-MM-DD HH:mm"),
      end_date: endDateTime.format("YYYY-MM-DD HH:mm"),
    };

    if (this.promo_code_value != "") {
      obj["promo_code"] = this.promo_code_value;
    }

    this.studioService.get_final_price(obj).subscribe((res) => {
      this.final_price = res.final_price;
      this.final_price_after_apply_promo = res.final_price_after_apply_promo;
      this.promo_code_is_valid = res.promo_code_validity;
      this.discount_periods_found = res.discount_periods_found;
      this.reduce_value = (
        this.final_price - this.final_price_after_apply_promo
      )
        .toFixed(2)
        .toString();
      Webflow.setIFrameHeight();
    });
    this.studioService.get_price_fees().subscribe((res) => {
      this.price_fees = Simulator.attributeToJson(res);
    });
  }

  back() {
    this.location.back();
    this.errors = null;
  }

  login() {
    this.params = { ...this.params, return: "payment-form" };

    // Navigate with the appropriate query parameters
    Webflow.myAction(this.router, "login", this.params);
  }

  get_params() {
    // Create the payload in the required format
    const data = {
      start_date: this.params["start_date"],
      end_date: this.params["end_date"],
      studio: this.currentStudio?.id, // Replace with the actual studio ID if needed
    };

    if (this.promo_code_is_valid && this.promo_code_value != "") {
      data["promo_code"] = this.promo_code_value;
    }

    return data;
  }

  navigateTo(actionName: string) {
    Webflow.myAction(this.router, actionName, {});
  }

  startPayment() {
    let data = this.get_params();
    // Function to check for overlapping dates
    this.reservationService.busyIntervals(data).subscribe((value) => {
      // check if the innterval is free
      if (!value) {
        Webflow.setIFrameHeight();
        Swal.fire({
          title: "Nouvelle réservation",
          text:
            "Étes-vous sûre de vouloir crée la reservation pour le studio '" +
            this.currentStudio.name +
            "' ?",
          icon: "info",
          showCancelButton: true,
          confirmButtonText: `OUI`,
          cancelButtonText: `NON`,
          cancelButtonColor: "black",
          confirmButtonColor: "#D63333",
          position: "top",
        }).then((result) => {
          if (result.isConfirmed) {
            this.reservationService
              .create(data)
              .subscribe(
                (res) => {
                  const token = res.id;
                  if (token != null) {
                    this.stripe.redirectToCheckout({
                      sessionId: token,
                    });
                  } else {
                    Swal.fire({
                      title: "Erreur",
                      text: "Une érreur est survenue",
                      icon: "error",
                      position: "top",
                    });
                  }
                },
                (error) => {
                  if (GenerateErrors.check_if_is_custom_error(error)) {
                    if (
                      GenerateErrors.get_error_id(error) == "phone.is.required"
                    ) {
                      Swal.fire({
                        title: "Attention",
                        text: GenerateErrors.get_message_error(error),
                        icon: "info",
                        showCancelButton: true,
                        confirmButtonText: "Mon profil",
                        cancelButtonText: "Fermer",
                        position: "top",
                        cancelButtonColor: "black",
                        confirmButtonColor: "#D63333",
                      }).then((result) => {
                        if (result.isConfirmed) {
                          var params = {};
                          this.activatedRoute.queryParams.subscribe(
                            (paramValues) => {
                              params = { ...paramValues };
                              params["return"] = "payment-form";
                              Webflow.myAction(
                                this.router,
                                "user-profile",
                                params
                              );
                            }
                          );
                        }
                      });
                    } else {
                      Swal.fire({
                        icon: "error",
                        title: "Erreur",
                        text: GenerateErrors.get_message_error(error),
                        position: "top",
                      });
                    }
                  } else {
                    Swal.fire({
                      icon: "error",
                      title: "Erreur",
                      text: "Une érreur est survenue",
                      position: "top",
                    });
                  }
                }
              )
              .add(() => {
                Webflow.setIFrameHeight();
              });
          }
        });
      } else {
        Webflow.myAction(this.router, "planning-booking", data);
      }
    });
  }

  changeDates() {
    Webflow.myAction(this.router, "planning-booking", this.get_params());
  }

  help() {
    Webflow.myAction(this.router, "payment-help", {});
  }
}
